import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { X, Sparkles, Lock, CheckCircle2, ShieldAlert } from "lucide-react";

/**
 * UnlockModal — simulated payment flow.
 *
 * TODO(Phase-8-billing): Replace the mock confirm() call with a Stripe
 * Elements form. Backend `POST /api/unlock/orders/{id}/confirm` will then
 * verify PaymentIntent status server-side before flipping status → succeeded.
 *
 * Props:
 *   open, onClose
 *   scope: "single_module" | "full_course"
 *   courseId, moduleId? (required for single_module)
 *   onUnlocked(unlockedModuleIds)
 */
export function UnlockModal({ open, onClose, scope, courseId, moduleId, onUnlocked }) {
  const { t, isRTL } = useI18n();
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | processing | success | error
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQuote(null);
    setOrder(null);
    setStatus("loading");
    setErrorMsg(null);
    (async () => {
      try {
        const q = await api.unlockQuote({
          course_id: courseId,
          scope,
          module_id: scope === "single_module" ? moduleId : undefined,
        });
        setQuote(q);
        setStatus("idle");
      } catch (e) {
        setStatus("error");
        setErrorMsg(mapReason(e.reason || e.message, t));
      }
    })();
  }, [open, scope, courseId, moduleId, t]);

  async function onConfirm() {
    setStatus("processing");
    setErrorMsg(null);
    try {
      // Idempotent: reuses a pending order if one exists (30-min window)
      const o = order || (await api.createOrder({
        course_id: courseId,
        scope,
        module_id: scope === "single_module" ? moduleId : undefined,
      }));
      setOrder(o);
      // Create Stripe Checkout Session and redirect to hosted checkout.
      const { checkout_url } = await api.createCheckout(o.order_id, window.location.origin);
      if (!checkout_url) throw new Error("no_checkout_url");
      window.location.href = checkout_url;
    } catch (e) {
      setStatus("error");
      setErrorMsg(mapReason(e.reason || e.message, t));
    }
  }

  async function onCancel() {
    if (order && status !== "processing") {
      try {
        await api.cancelOrder(order.order_id);
      } catch {
        // ignore
      }
    }
    onClose?.();
  }

  if (!open) return null;

  const title = scope === "full_course" ? t("unlock.titleFull") : t("unlock.title");
  const subtitle = scope === "full_course" ? t("unlock.subtitleFull") : t("unlock.subtitleSingle");

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="unlock-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={status === "success" ? onClose : onCancel} />
      <div className="relative w-full max-w-md rounded-[2rem] bg-white border-2 border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.30)] overflow-hidden">
        {/* Header */}
        <div className="relative p-6 bg-gradient-to-br from-[#E7EEFE] to-[#DFF6FD]">
          <button
            onClick={status === "processing" ? undefined : (status === "success" ? onClose : onCancel)}
            disabled={status === "processing"}
            className="absolute top-4 end-4 h-8 w-8 grid place-items-center rounded-full bg-white/80 hover:bg-white transition disabled:opacity-50"
            data-testid="unlock-close"
            aria-label={t("unlock.cancel")}
          >
            <X className="h-4 w-4" />
          </button>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-[#FFB703] font-black px-3 py-1 text-xs uppercase tracking-wider">
            <Lock className="h-3.5 w-3.5" /> {scope === "full_course" ? t("courseDetail.paid") : t("courseDetail.paid")}
          </div>
          <h2 className="mt-3 text-2xl md:text-3xl font-black tracking-tight" data-testid="unlock-title">{title}</h2>
          <p className="mt-1 text-sm text-black/70 font-medium">{subtitle}</p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {status === "loading" && (
            <div className="h-24 rounded-2xl bg-black/5 animate-pulse" />
          )}

          {quote && status !== "success" && (
            <>
              <div>
                <div className="text-xs font-black uppercase tracking-wider text-black/50 mb-2">
                  {t("unlock.included")}
                </div>
                <ul className="rounded-2xl bg-[#F3F0E9] border-2 border-black/5 divide-y divide-black/5 max-h-56 overflow-y-auto" data-testid="unlock-included">
                  {(quote.module_titles || []).map((m) => (
                    <li key={m.module_id} className="flex items-center gap-3 px-4 py-3 text-sm">
                      <span className="h-7 w-7 rounded-full bg-black/5 grid place-items-center font-black text-xs">{m.order}</span>
                      <span className="flex-1 font-semibold text-[#1A1A1A] truncate">
                        {m.title[isRTL ? "ar" : "en"] || m.title.en}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex items-baseline justify-between">
                <span className="text-sm font-bold text-black/60">{quote.currency_display}</span>
                <span className="text-4xl font-black tracking-tight" data-testid="unlock-price">{quote.amount_display}</span>
              </div>

              <div className="flex items-start gap-2 rounded-2xl bg-[#FEF3C7] text-[#7A4B00] px-4 py-3 text-xs font-bold" data-testid="unlock-demo-banner">
                <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t("unlock.demoBanner")}</span>
              </div>
            </>
          )}

          {status === "error" && errorMsg && (
            <div className="rounded-2xl bg-[#FEE2E2] text-[#991B1B] px-4 py-3 text-sm font-semibold" data-testid="unlock-error">
              {errorMsg}
            </div>
          )}

          {status === "success" && (
            <div className="text-center py-6" data-testid="unlock-success">
              <div className="mx-auto h-16 w-16 rounded-full bg-[#DCFCE7] text-[#166534] grid place-items-center">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <div className="mt-4 text-2xl font-black">{t("unlock.successToast")}</div>
              <div className="mt-1 text-sm text-black/60 font-medium">
                {order?.provider_ref && `Ref: ${order.provider_ref.slice(0, 18)}…`}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t-2 border-black/5 bg-white flex items-center gap-2">
          {status === "success" ? (
            <button
              onClick={onClose}
              className="w-full rounded-full bg-[#1A1A1A] text-white font-bold px-6 py-3 shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition inline-flex items-center justify-center gap-2"
              data-testid="unlock-done"
            >
              <Sparkles className="h-4 w-4" /> {scope === "single_module" ? t("unlock.openModule") : t("unlock.backToCourse")}
            </button>
          ) : (
            <>
              <button
                onClick={onCancel}
                disabled={status === "processing"}
                className="flex-1 rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-4 py-3 text-sm disabled:opacity-50 transition"
                data-testid="unlock-cancel"
              >
                {t("unlock.cancel")}
              </button>
              <button
                onClick={onConfirm}
                disabled={status === "processing" || status === "loading" || !quote || status === "error"}
                className="flex-1 rounded-full bg-[#4361EE] hover:bg-[#4361EE]/90 disabled:opacity-60 text-white font-bold px-4 py-3 text-sm shadow-[0_3px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_2px_0_#2A3EB1] transition"
                data-testid="unlock-confirm"
              >
                {status === "processing" ? t("unlock.processing") : t("unlock.confirm")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function mapReason(reason, t) {
  if (!reason) return t("unlock.errorGeneric");
  if (reason === "already_unlocked") return t("unlock.errorAlreadyUnlocked");
  if (reason === "exam_not_purchasable") return t("unlock.errorExamNotPurchasable");
  if (reason === "stripe_key_expired" || reason === "stripe_key_invalid")
    return t("unlock.errorPaymentTemporarilyUnavailable");
  if (reason === "stripe_rate_limited") return t("unlock.errorPaymentBusy");
  if (reason === "stripe_error") return t("unlock.errorPaymentGeneric");
  return t("unlock.errorGeneric");
}
